import { plainToClass } from "class-transformer";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import type { Route } from "../../../../../utils/models/Route";
import { remapDestinationsAfterReversal, type StageDestination } from "../../../services/stage-destinations";
import type { UndoCommand, CommandContext } from "../types";

interface ReversibleRoute {
  markers: any[];
  lineStrings: any[];
  updated_at?: Date;
}

interface DestinationState {
  routes: { [routeKey: string]: Route };
  stageDestinations?: { [dayNumber: number]: StageDestination };
  trailStartDestination?: StageDestination | null;
}

/** Reverse a route in place: marker order, segment order, and each segment's own geometry and turns. */
export function reverseRouteInPlace(route: ReversibleRoute): void {
  route.markers.reverse();
  route.lineStrings = route.lineStrings.map(lineString => {
    const instance = plainToClass(LineStringFeature, lineString);
    return instance.reverse();
  }).reverse();
  route.updated_at = new Date();
}

/**
 * Stage-destination (POI/waypoint/highlight) attachments, including the
 * trail-start destination, are validated at render time by coordinate
 * proximity. Whenever a route's endpoints move — a reverse, or undoing one —
 * re-key `stageDestinations`/`trailStartDestination` onto whichever anchor now
 * sits on them, otherwise the prune would drop them.
 */
export function remapStageDestinations(state: DestinationState): void {
  const hasStageDestinations = state.stageDestinations && Object.keys(state.stageDestinations).length > 0;
  if (!hasStageDestinations && !state.trailStartDestination) return;

  const remapped = remapDestinationsAfterReversal(
    state.routes,
    state.stageDestinations || {},
    state.trailStartDestination || null
  );
  state.stageDestinations = remapped.stageDestinations;
  state.trailStartDestination = remapped.trailStartDestination;
}

/**
 * Reverse a single route AND keep stage-destination attachments intact. If the
 * reversed route carried the trail start or a day's stage end, reversing it
 * swaps which of its endpoints sits there.
 */
export function reverseRouteStateInPlace(state: DestinationState, routeKey: string): void {
  const route = state.routes[routeKey];
  if (!route) return;
  reverseRouteInPlace(route);
  remapStageDestinations(state);
}

/**
 * Command for reversing a route.
 *
 * Not self-inverse: reversing mirrors each segment's turns, and mirroring drops what cannot be
 * inverted — GraphHopper's wording, cues, a person's re-worded text. Reversing twice therefore
 * does not restore the original, so undo restores a snapshot taken before the reverse. Redo
 * restores one taken after it, because bike and car segments were routed again and reversing
 * once more would mirror them instead. Both are deep copies so later edits cannot reach them.
 *
 * Stage destinations are remapped by coordinate on both undo and redo, so they follow the
 * route's endpoints back and forth.
 */
export class ReverseRouteCommand implements UndoCommand {
  readonly type = 'REVERSE_ROUTE';
  readonly routeKey: string;

  private readonly markersBefore: any[];
  private readonly lineStringsBefore: any[];
  private readonly markersAfter: any[];
  private readonly lineStringsAfter: any[];

  constructor(routeKey: string, routeBefore: ReversibleRoute, routeAfter: ReversibleRoute) {
    this.routeKey = routeKey;
    this.markersBefore = JSON.parse(JSON.stringify(routeBefore.markers));
    this.lineStringsBefore = JSON.parse(JSON.stringify(routeBefore.lineStrings));
    this.markersAfter = JSON.parse(JSON.stringify(routeAfter.markers));
    this.lineStringsAfter = JSON.parse(JSON.stringify(routeAfter.lineStrings));
  }

  undo(ctx: CommandContext): void {
    const route = ctx.state.routes[this.routeKey];
    if (!route) return;

    route.markers = JSON.parse(JSON.stringify(this.markersBefore));
    route.lineStrings = JSON.parse(JSON.stringify(this.lineStringsBefore));
    route.updated_at = new Date();
    remapStageDestinations(ctx.state);
  }

  redo(ctx: CommandContext): void {
    const route = ctx.state.routes[this.routeKey];
    if (!route) return;

    route.markers = JSON.parse(JSON.stringify(this.markersAfter));
    route.lineStrings = JSON.parse(JSON.stringify(this.lineStringsAfter));
    route.updated_at = new Date();
    remapStageDestinations(ctx.state);
  }
}
